/**
 * usePlaybook — загружает скрипты/инструкции по стадиям GET /api/playbook.
 * В DEV возвращает встроенный плейбук без сетевого запроса.
 */

import { useQuery } from '@tanstack/react-query'
import { playbookApi } from '@/api/client'
import type { Playbook } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const MOCK_PLAYBOOK: Playbook = {
  new_lead: {
    title: 'Новый лид',
    goal: 'Взять лид в работу, убедиться что не двойник, связаться в течение 30 мин',
    do: [
      'Прочитать первое сообщение и определить нишу',
      'Проверить: не дубль (телефон/имя)',
      'Нажать «Взял в работу»',
      'Позвонить или написать в течение 30 минут',
    ],
    script: {
      open: 'Добрый день! Меня зовут [имя], компания TouchBrand. Вы оставили заявку на автоматизацию общения с клиентами — всё верно?',
      questions: [
        'Расскажите немного о вашем бизнесе — чем занимаетесь?',
        'Как сейчас обрабатываете входящие обращения?',
        'Что вас больше всего не устраивает в текущей ситуации?',
      ],
      pitch: 'Мы настраиваем цифрового сотрудника на базе ИИ — он отвечает клиентам 24/7, записывает, консультирует и не устаёт. Вы освобождаете себя от рутины.',
      objections: [
        { q: 'Сколько стоит?', a: 'Стоимость зависит от каналов и задач. Давайте сначала разберёмся с вашей ситуацией, потом назову точную цифру.' },
        { q: 'Мне надо подумать', a: 'Конечно. Что именно нужно обдумать? Может, есть вопросы, на которые я могу ответить прямо сейчас?' },
      ],
      close: 'Предлагаю встретиться/созвониться на 30 минут — разберём вашу ситуацию и покажу, что именно можем сделать. Когда удобно?',
    },
    tools: ['claim', 'release'],
    next: 'conversation',
  },
  conversation: {
    title: 'Разговор',
    goal: 'Выяснить бизнес/каналы/системы/боль, собрать конфигурацию, назвать цену, закрыть',
    do: [
      'Пройти по скрипту: бизнес → каналы → системы/CRM → боль → готовность',
      'Открыть конфигуратор: каналы, интеграции, модули, прогноз обращений',
      'Назвать цену клиенту прямо на звонке (договор 50/50 + подписка)',
      'Рассказать как заключить: предоплата 50%, гарантия запуска',
      'Закрыли → Договор. Думает → отправить КП. Не подходит → Отказ',
    ],
    script: {
      open: 'Спасибо, что на связи. Задам несколько коротких вопросов — и сразу посчитаю цену.',
      questions: [
        'Чем занимается бизнес — что продаёте, кому?',
        'Куда сейчас пишут клиенты — Telegram, VK, Авито, сайт?',
        'Какими системами пользуетесь — CRM, запись, таблицы?',
        'Сколько обращений в месяц примерно?',
        'Что болит больше всего — теряете заявки, не успеваете, рутина?',
      ],
      pitch: 'Цифровой сотрудник отвечает 24/7, принимает заявки, не теряет обращения. Внедрение двумя частями, подписка от 4900/мес, запуск 10–14 рабочих дней, гарантия запуска.',
      objections: [
        { q: 'Дорого', a: 'Давайте посчитаем: даже одна спасённая заявка окупает подписку. Плюс внедрение двумя частями и гарантия запуска.' },
        { q: 'Надо подумать', a: 'Конечно. Что смущает — цена, сроки? Отправлю КП с расчётом и наберу завтра.' },
      ],
      close: 'Если по цене ок — оформляем договор, предоплата 50%, и я запускаю работу. Хотите глянуть на бумаге — отправлю КП.',
    },
    tools: ['configuration_form', 'stage_transition'],
    next: 'won',
  },
  proposal_sent: {
    title: 'КП отправлено',
    goal: 'Отправить клиенту КП, получить обратную связь, закрыть в оплату',
    do: [
      'Сформировать КП (кнопка ниже)',
      'Скачать PDF и отправить клиенту',
      'Позвонить через 1–2 дня для обсуждения',
      'При согласии — перевести в «Выиграна»',
    ],
    script: {
      open: 'Добрый день! Отправил вам КП [дата]. Успели посмотреть?',
      questions: [
        'Есть ли вопросы по составу или стоимости?',
        'Что показалось наиболее интересным?',
        'Что смущает или вызывает сомнения?',
      ],
      pitch: 'Наш цифровой сотрудник окупается за 2–4 месяца. После запуска вы платите только подписку и расширяете каналы по мере роста.',
      objections: [
        { q: 'Подумаю ещё', a: 'Сколько времени нужно? Обычно клиенты решаются за 3–5 дней. Что мешает принять решение сейчас?' },
        { q: 'Есть дешевле', a: 'Расскажите подробнее — что предлагают и за какую цену? Сравним по составу.' },
      ],
      close: 'Если решение принято — нам нужен аванс 50%, и мы стартуем на этой неделе. Реквизиты в счёте.',
    },
    tools: ['proposal'],
    next: 'won',
  },
  won: {
    title: 'Выиграна',
    goal: 'Выставить счёт на первый транш, получить оплату, запустить внедрение',
    do: [
      'Сформировать счёт (1-й транш, 50%)',
      'Отправить счёт клиенту',
      'Отметить получение оплаты',
      'Нажать «Начать внедрение» → переход в Onboarding',
    ],
    script: {
      open: 'Отличная новость — начинаем работу! Отправил вам счёт на первый транш.',
      questions: [
        'Есть ли вопросы по реквизитам?',
        'Когда планируете оплатить?',
      ],
      pitch: 'После получения предоплаты мы приступаем к сборке за 2–3 недели.',
      objections: [
        { q: 'Можно позже оплатить?', a: 'Мы стартуем после получения первого транша. Чем раньше — тем быстрее запуск.' },
      ],
      close: 'Как только поступит оплата — назначу вам менеджера проекта и начнём онбординг.',
    },
    tools: ['invoice', 'payment', 'stage_transition'],
    next: 'onboarding',
  },
  onboarding: {
    title: 'Онбординг',
    goal: 'Создать кабинет клиента, собрать базу знаний, подключить каналы',
    do: [
      'Создать кабинет клиента (кнопка ниже)',
      'Получить материалы от клиента (прайс, FAQ, скрипты)',
      'Собрать базу знаний',
      'Настроить основной канал',
    ],
    script: {
      open: 'Отлично, оплата получена! Начинаем. Первый шаг — создать ваш кабинет и собрать базу знаний.',
      questions: [
        'Пришлите прайс-лист и список частых вопросов от клиентов.',
        'Есть ли скрипты продаж или записи разговоров?',
        'Какой у вас Telegram-бот или нужно создать новый?',
      ],
      pitch: '',
      objections: [],
      close: 'Через 2–3 дня после получения материалов покажем первую версию бота.',
    },
    tools: ['provision'],
    next: 'build',
  },
  testing: {
    title: 'Тестирование',
    goal: 'Провести тест-прогон, набрать ≥27/30 баллов, получить одобрение клиента',
    do: [
      'Запустить тест-прогон по 30 сценариям',
      'Ввести результат (балл 0–30)',
      'При ≥27 — Тест-гейт пройден, можно запускать',
      'При <27 — вернуть на доработку',
    ],
    script: {
      open: 'Тестирование завершено. Показываем результаты клиенту.',
      questions: [
        'Всё ли отвечает так, как ожидали?',
        'Есть ли сценарии, которые бот обрабатывает не так?',
      ],
      pitch: 'Мы прогнали бот по 30 реальным сценариям вашей ниши. Результат [балл]/30.',
      objections: [
        { q: 'Хочу поправить некоторые ответы', a: 'Конечно, скажите что именно — внесём правки в базу знаний.' },
      ],
      close: 'После вашего одобрения переходим к прод-запуску.',
    },
    tools: ['test_run'],
    next: 'launch',
  },
  build: {
    title: 'Сборка — наполнить базу знаний',
    goal: 'Заполнить 7 обязательных топиков БЗ по материалам клиента и получить его одобрение (Ворота 2).',
    do: [
      'Заполнить 7 обязательных топиков БЗ: о компании, продукт, цены, процесс, FAQ, правила передачи, системный промпт',
      'Показать клиенту содержание, получить одобрение каждого топика',
      'Все 7 топиков approved → Ворота 2 пройдены → Интеграция',
    ],
    script: { open: '', questions: [], pitch: '', objections: [], close: '' },
    tools: ['Редактор БЗ (7 топиков)', 'Кнопка «Одобрить»'],
    next: 'integration',
  },
  integration: {
    title: 'Интеграция — подключить каналы',
    goal: 'Подключить цифрового сотрудника ко всем оплаченным каналам и настроить передачу заявок оператору.',
    do: [
      'Настроить бота в каждом канале из договора (Telegram, VK, Avito, MAX)',
      'Настроить owner-бот: токен + список операторских chat_id',
      'Проверить передачу тестовой заявки → уведомление оператору',
      'channels_verified → Тестирование',
    ],
    script: { open: '', questions: [], pitch: '', objections: [], close: '' },
    tools: ['Панель каналов', 'Owner-бот (provision-client)'],
    next: 'testing',
  },
  launch: {
    title: 'Запуск — включить боевой режим',
    goal: 'Перевести бота из тестового в рабочий режим и убедиться, что реальные обращения обрабатываются корректно.',
    do: [
      'Убедиться, что gate_3 пройдены (tenant + токен + операторы настроены)',
      'Выполнить channels_cutover — переключить каналы на боевой поток',
      'Уведомить клиента о запуске, отследить первые 5–10 диалогов',
      'Зафиксировать дату запуска → Гиперкэр (14 дней)',
    ],
    script: { open: '', questions: [], pitch: '', objections: [], close: '' },
    tools: ['action channels_cutover', 'Мониторинг диалогов'],
    next: 'hypercare',
  },
  hypercare: {
    title: 'Гиперкэр — 14 дней активной поддержки',
    goal: 'Максимальная поддержка в первые две недели работы, исправление проблем день в день. По итогам — 2-й транш.',
    do: [
      'Мониторить диалоги ежедневно, реагировать на вопросы клиента в течение рабочего дня',
      'При повторяющихся ошибках — обновить БЗ',
      'Через 14 дней подвести итог, выставить счёт на 2-й транш',
      'После оплаты — перевести в Активный (подписка)',
    ],
    script: { open: '', questions: [], pitch: '', objections: [], close: '' },
    tools: ['Мониторинг диалогов', 'Счёт 2-й транш', 'action reminder_second_tranche'],
    next: 'active',
  },
  active: {
    title: 'Активный клиент — сопровождение',
    goal: 'Клиент на подписке. Поддерживать качество бота, мониторить здоровье, предлагать апсейлы при случае.',
    do: [
      'Раз в месяц — анализ переписок, обновление БЗ (входит в подписку)',
      'При жалобах — реагировать в течение рабочего дня',
      'При росте объёма — предложить расширенный пакет или апсейл-модуль',
      'Раз в квартал — короткий созвон: как дела, что улучшить',
    ],
    script: { open: '', questions: [], pitch: '', objections: [], close: '' },
    tools: ['Аналитика диалогов', 'Модули апсейлов из каталога'],
    next: null,
  },
}

export const playbookKeys = {
  all: ['playbook'] as const,
}

export function usePlaybook() {
  return useQuery({
    queryKey: playbookKeys.all,
    queryFn: async (): Promise<Playbook> => {
      if (USE_MOCK) return MOCK_PLAYBOOK
      return playbookApi.get()
    },
    staleTime: 15 * 60 * 1000, // 15 мин
  })
}
